import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { tap } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Dev-only network logger.
 *
 * Prints every HTTP call to the browser console as a collapsed group — method,
 * URL (with query params), request body, response status, body and round-trip
 * time. Failures are logged in red with the error payload. It's a no-op in
 * production builds and during SSR, and registered FIRST so its timing brackets
 * the whole interceptor chain (auth header, token refresh, etc.).
 */
export const loggingInterceptor: HttpInterceptorFn = (req, next) => {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  if (environment.production || !isBrowser) {
    return next(req);
  }

  const started = Date.now();
  const label = `${req.method} ${req.urlWithParams}`;
  // Never print credentials, even on a dev console: the login request carries the raw
  // password and the login/refresh responses carry the JWT + refresh token. A screenshot
  // or screen-share of DevTools must not be a credential leak.
  const isAuthCall = req.url.includes('/Login/');
  const body = isAuthCall
    ? '[redacted: auth request]'
    : req.body instanceof FormData
      ? '[FormData]'
      : req.body;

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          const ms = Date.now() - started;
          console.groupCollapsed(
            `%c⇄ ${event.status} %c${label} %c(${ms}ms)`,
            'color:#15803d;font-weight:700',
            'color:#1f2937',
            'color:#6b7280',
          );
          if (body != null) console.log('request ', body);
          console.log('response', isAuthCall ? '[redacted: auth response]' : event.body);
          console.groupEnd();
        }
      },
      error: (err) => {
        const ms = Date.now() - started;
        const status = err instanceof HttpErrorResponse ? err.status : 0;
        console.groupCollapsed(
          `%c✖ ${status} %c${label} %c(${ms}ms)`,
          'color:#b91c1c;font-weight:700',
          'color:#1f2937',
          'color:#6b7280',
        );
        if (body != null) console.log('request', body);
        console.log('error   ', err instanceof HttpErrorResponse ? (err.error ?? err.message) : err);
        console.groupEnd();
      },
    }),
  );
};
